import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RechtSeitenRahmen } from "@/components/recht/RechtSeitenRahmen";

// Widerrufsbelehrung und Muster-Widerrufsformular (Sitzung „Aufräumen“, 14.10.2026). Bis dahin verlinkten die
// Themenreise-Kasse und die AGB eine Seite /widerruf, die es nicht gab (404). Grundlage sind die gesetzlichen Muster
// (Anlagen 1 und 2 zu Art. 246a EGBGB) mit dem Hinweis auf die Schaltfläche „Vertrag widerrufen“ (§ 356a BGB) und
// dem Fristbeginn bei Fernlehrgängen. Text von Claude, Prüfung durch Daniel oder die Anwältin.
const ANSCHRIFT = "MusiKonzept, Daniel Sommer, Lanker Straße 10, 40545 Düsseldorf";

const Widerruf = () => (
  <RechtSeitenRahmen
    titel="Widerrufsbelehrung"
    beschreibung="Dein Widerrufsrecht bei MusiKonzept: Frist, Folgen des Widerrufs und das Muster-Widerrufsformular."
    pfad="/widerruf"
  >
    <div className="space-y-10 text-muted-foreground leading-relaxed">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold text-primary">Widerrufsbelehrung</h1>
        <p>
          Verbraucherinnen und Verbraucher (§ 13 BGB) haben bei Verträgen, die sie mit uns online, per E-Mail oder
          telefonisch schließen, ein gesetzliches Widerrufsrecht. Für Verträge mit Einrichtungen, Trägern oder
          Unternehmen (zum Beispiel das Musik-Kita-Paket für eine Kita) gilt es nicht.
        </p>
        <div className="flex flex-wrap gap-3 pt-2 print:hidden">
          <Button asChild className="min-h-[44px] rounded-full bg-[#227277] text-white hover:bg-[#1D2934]">
            <Link to="/vertrag-widerrufen">Vertrag widerrufen</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px] rounded-full border-[#227277] text-[#227277]"
            onClick={() => window.print()}
          >
            <Printer className="mr-2 h-4 w-4" />
            Drucken oder als PDF speichern
          </Button>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Widerrufsrecht</h2>
        <p>Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.</p>
        <p>
          Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses. Bei Fernlehrgängen (zum Beispiel
          „Fachkraft für Musik im Elementarbereich“) beginnt die Frist nicht vor dem Zugang der ersten Lieferung des
          Fernlehrmaterials, also der Freischaltung des Lehrgangs in der Online-Akademie.
        </p>
        <p>
          Um Ihr Widerrufsrecht auszuüben, müssen Sie uns ({ANSCHRIFT}, Telefon 0211 33676286, E-Mail{" "}
          <a href="mailto:info@musikonzept.de" className="text-primary hover:underline">
            info@musikonzept.de
          </a>
          ) mittels einer eindeutigen Erklärung (zum Beispiel ein mit der Post versandter Brief oder eine E-Mail) über
          Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das unten stehende
          Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist. Sie können den Widerruf auch über die
          Schaltfläche{" "}
          <Link to="/vertrag-widerrufen" className="text-primary hover:underline">
            „Vertrag widerrufen“
          </Link>{" "}
          auf unserer Webseite erklären; dann bestätigen wir Ihnen den Eingang unverzüglich mit Datum und Uhrzeit per
          E-Mail.
        </p>
        <p>
          Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor
          Ablauf der Widerrufsfrist absenden.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Folgen des Widerrufs</h2>
        <p>
          Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben,
          einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich daraus ergeben, dass Sie eine
          andere Art der Lieferung als die von uns angebotene, günstigste Standardlieferung gewählt haben), unverzüglich
          und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses
          Vertrags bei uns eingegangen ist. Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der
          ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart;
          in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.
        </p>
        <p>
          Haben Sie verlangt, dass die Dienstleistungen während der Widerrufsfrist beginnen sollen, so haben Sie uns einen
          angemessenen Betrag zu zahlen, der dem Anteil der bis zu dem Zeitpunkt, zu dem Sie uns von der Ausübung des
          Widerrufsrechts hinsichtlich dieses Vertrags unterrichten, bereits erbrachten Dienstleistungen im Vergleich zum
          Gesamtumfang der im Vertrag vorgesehenen Dienstleistungen entspricht.
        </p>
        <p>
          Haben wir Ihnen Waren geliefert (zum Beispiel Bücher), können wir die Rückzahlung verweigern, bis wir die Waren
          wieder zurückerhalten haben oder bis Sie den Nachweis erbracht haben, dass Sie die Waren zurückgesandt haben, je
          nachdem, welches der frühere Zeitpunkt ist. Sie haben die Waren unverzüglich und in jedem Fall spätestens binnen
          vierzehn Tagen ab dem Tag, an dem Sie uns über den Widerruf dieses Vertrags unterrichten, an uns zurückzusenden
          oder zu übergeben. Die Frist ist gewahrt, wenn Sie die Waren vor Ablauf der Frist von vierzehn Tagen absenden.
          Sie tragen die unmittelbaren Kosten der Rücksendung der Waren. Sie müssen für einen etwaigen Wertverlust der
          Waren nur aufkommen, wenn dieser Wertverlust auf einen zur Prüfung der Beschaffenheit, Eigenschaften und
          Funktionsweise der Waren nicht notwendigen Umgang mit ihnen zurückzuführen ist.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-2xl font-bold text-foreground">Besondere Hinweise</h2>
        <p>
          Bei einem Vertrag über digitale Inhalte, die nicht auf einem körperlichen Datenträger geliefert werden, erlischt
          das Widerrufsrecht vorzeitig, wenn wir mit der Ausführung des Vertrags begonnen haben, nachdem Sie ausdrücklich
          zugestimmt haben, dass wir vor Ablauf der Widerrufsfrist mit der Ausführung beginnen, und Sie Ihre Kenntnis davon
          bestätigt haben, dass Sie durch Ihre Zustimmung mit Beginn der Ausführung Ihr Widerrufsrecht verlieren. Das gilt
          nur, wenn Sie diese Zustimmung bei der Buchung gegeben haben.
        </p>
        <p>
          Bei Taler-Paketen beginnen wir mit der Ausführung, sobald die Musiktaler Ihrem Konto gutgeschrieben sind. Beim
          Kauf stimmen Sie dem ausdrücklich zu und bestätigen, dass Sie Ihr Widerrufsrecht damit verlieren. Ohne diese
          Zustimmung können Sie ein Taler-Paket nicht kaufen.
        </p>
        <p>
          Kündigen statt widerrufen: Eine laufende Mitgliedschaft kündigen Sie unabhängig von der Widerrufsfrist in Ihrem
          Bereich unter{" "}
          <Link to="/mein-bereich/einstellungen" className="text-primary hover:underline">
            „Mein Account“
          </Link>{" "}
          → „Abo &amp; Rechnungen“ → „Abo kündigen“.
        </p>
      </section>

      <section id="muster-widerrufsformular" className="space-y-3 rounded-xl border border-border bg-card p-6 text-foreground">
        <h2 className="text-2xl font-bold">Muster-Widerrufsformular</h2>
        <p className="text-muted-foreground">
          (Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es zurück.)
        </p>
        <ul className="space-y-3">
          <li>– An {ANSCHRIFT}, E-Mail: info@musikonzept.de:</li>
          <li>
            – Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über den Kauf der folgenden
            Waren (*) / die Erbringung der folgenden Dienstleistung (*)
            <span className="mt-2 block h-6 border-b border-dashed border-border" />
          </li>
          <li>
            – Bestellt am (*) / erhalten am (*)
            <span className="mt-2 block h-6 border-b border-dashed border-border" />
          </li>
          <li>
            – Name des/der Verbraucher(s)
            <span className="mt-2 block h-6 border-b border-dashed border-border" />
          </li>
          <li>
            – Anschrift des/der Verbraucher(s)
            <span className="mt-2 block h-6 border-b border-dashed border-border" />
          </li>
          <li>
            – Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier)
            <span className="mt-2 block h-6 border-b border-dashed border-border" />
          </li>
          <li>
            – Datum
            <span className="mt-2 block h-6 border-b border-dashed border-border" />
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">(*) Unzutreffendes streichen.</p>
      </section>
    </div>
  </RechtSeitenRahmen>
);

export default Widerruf;
